import React, {
	useState,
	useEffect,
	useRef,
	FocusEvent,
	ChangeEvent
} from 'react';
import { useFormContext } from 'react-hook-form';

import {
	IconFeedbackLoadingComponent,
	IconTravelAirportComponent,
	IconTravelHotelComponent,
	IconTravelMapMarkerComponent,
	IconsProps
} from '@vaa-component-lib/component.atom.icons';
import { TextFieldComponent } from '@vaa-component-lib/component.atom.text-field';
import {
	TypographyComponent,
	TypographySize,
	TypographyVariant,
	TypographyWeight
} from '@vaa-component-lib/component.atom.typography';

import sanitiseString from '../../lib/utils/sanitise-string';
import {
	getAirportsByTerm,
	getCJSLocationSearchUrl
} from '../../lib/utils/api.service';

import { useDebounce } from '../../hooks/useDebounce';
import useMediaQuery, { BreakPointToken } from '../../hooks/useMediaQuery';
import { validateLocations } from '../../lib/utils/validate-locations.service';

import { Airport, LocationParams, Location } from '../../types/api';

import CriteriaTriggerComponent from '../criteria-trigger/criteria-trigger.component';
import PopOverComponent, {
	PopoverFeedback,
	PopOverType
} from '../pop-over/pop-over.component';

import {
	TypeaheadResultsList,
	TypeaheadResult,
	TypeaheadResultButton,
	TypeaheadResultLabel,
	TypeaheadMobileInput,
	TypeaheadRecentTitle,
	TypeaheadSearching
} from './typeahead.styles';

interface TypeaheadComponentProps {
	label?: string;
	name?: string;
	popoverTitle?: string;
	placeholder?: string;
	historyTitle?: string;
	noResultsLabel: string;
	iconChild?: React.ReactElement<IconsProps>;
	id?: string;
	onChange: (e: ChangeEvent<HTMLInputElement>) => void;
	onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
	value?: Airport | Location;
	hasError?: boolean;
	feedback?: string;
	switchFocusTo?: () => void;
	locationSearch?: LocationParams;
}

type PopOverTriggerTypes =
	| 'criteriaClick'
	| 'popOverClose'
	| 'resultClick'
	| 'recentClick'
	| 'inputFocus'
	| 'inputClear'
	| 'componentBlur'
	| 'searchChange';

const TypeaheadComponent = ({
	label,
	name,
	placeholder,
	popoverTitle,
	historyTitle,
	noResultsLabel,
	iconChild,
	id = '',
	onChange,
	onBlur,
	value,
	hasError,
	feedback,
	switchFocusTo,
	locationSearch
}: TypeaheadComponentProps) => {
	const { setValue, clearErrors } = useFormContext();
	const [showPopOver, setShowPopOver] = useState<boolean>(false);
	const [showResults, setShowResults] = useState<boolean>(false);
	const [showRecentSearches, setShowRecentSearches] = useState<boolean>(false);
	const [suggestions, setSuggestions] = useState<Airport[] | Location[]>([]);
	const [initialSelection, setInitialSelection] = useState<string>('');
	const [userSelection, setUserSelection] = useState<string>('');
	const [inputValue, setInputValue] = useState<Airport | Location | null>(
		value || null
	);
	const searchQuery = useDebounce(userSelection, 250);
	const [selectedResult, setSelectedResult] = useState<string>('');
	const [noResults, setNoResults] = useState<boolean>(false);
	const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
	const [selectedAirports, setSelectedAirports] = useState<
		Airport[] | Location[]
	>([]);
	const [isSearching, setIsSearching] = useState<boolean>(false);
	const [isPrepoluated, setIsPrepoluated] = useState<boolean>(false);

	const isMobileLayout = useMediaQuery(
		`(max-width: ${BreakPointToken.SmMax}px)`
	);
	const popOverId = `popover-${sanitiseString(label || '')}`;
	const characterMin = 3;
	const recentMax = 3;

	const localStorageKey = `${id.replace('_', '-')}-searches`;

	const handleFocus = (e: FocusEvent<HTMLInputElement>): void => {
		handlePopOver('inputFocus');
	};

	const handleClear = (): void => {
		handlePopOver('inputClear');
		setUserSelection('');
		setInputValue(null);
		setValue(name, null, {
			shouldValidate: true,
			shouldDirty: true
		});
	};

	const handleBlur = (e: FocusEvent<HTMLInputElement>): void => {
		if (e.currentTarget.contains(e.relatedTarget as Node)) {
			return;
		}

		handlePopOver('componentBlur');

		if (selectedResult.length < 1 && !isMobileLayout && !isPrepoluated) {
			handleClear();
		}

		if (onBlur) {
			onBlur(e);
		}
	};

	const handleResultClick = (
		airport: Airport | Location,
		triggerType: PopOverTriggerTypes
	) => {
		const formattedLabel = `${(airport as Airport).cityName?.trim()}, ${(airport as Airport).region} (${(airport as Airport).airportCode})`;
		locationSearch
			? storeSearch(airport as Location)
			: storeSearch(airport as Airport);
		locationSearch
			? setUserSelection((airport as Location).indexedDescription)
			: setUserSelection(formattedLabel);
		locationSearch
			? setSelectedResult((airport as Location).indexedDescription)
			: setSelectedResult(formattedLabel);
		locationSearch
			? setInputValue(airport as Location)
			: setInputValue(airport as Airport);
		setValue(name, airport, {
			shouldValidate: true,
			shouldDirty: true
		});
		handlePopOver(triggerType);

		if (switchFocusTo && !isMobileLayout) {
			setTimeout(() => switchFocusTo(), 50);
		}
	};

	const handlePopOver = (trigger: PopOverTriggerTypes): void => {
		switch (trigger) {
			case 'criteriaClick':
				return setShowPopOver(true);
			case 'popOverClose':
				return setShowPopOver(false);
			case 'resultClick':
				return setShowPopOver(false);
			case 'recentClick':
				return setShowPopOver(false);
			case 'inputFocus':
				return (
					clearErrors(name),
					setShowPopOver(
						isMobileLayout ||
							isPrepoluated ||
							selectedAirports.length > 0 ||
							(searchQuery?.trim().length >= characterMin &&
								suggestions.length > 0)
					)
				);
			case 'inputClear':
				return setShowPopOver(isMobileLayout);
			case 'componentBlur':
				return !isMobileLayout && setShowPopOver(false);
			case 'searchChange':
				if (selectedResult.length > 1 || isPrepoluated) {
					return setShowPopOver(false);
				}
				return (
					!isMobileLayout &&
					(initialSelection !== userSelection || selectedResult.length < 1) &&
					setShowPopOver(
						searchQuery?.trim().length >= characterMin &&
							(suggestions.length > 0 ||
								selectedAirports.length > 0 ||
								noResults)
					)
				);
		}
	};

	const storeSearch = (thisAirport: Airport | Location): void => {
		if (locationSearch) {
			setSelectedAirports((prevSelectedAirports: (Location | Airport)[]) => {
				const filteredAirports = prevSelectedAirports.filter(
					(airport) =>
						(airport as Location).indexedDescription !==
						(thisAirport as Location).indexedDescription
				);
				return [...filteredAirports, thisAirport].slice(
					-1 * recentMax
				) as Location[];
			});
		} else {
			setSelectedAirports((prevSelectedAirports: (Location | Airport)[]) => {
				const filteredAirports = prevSelectedAirports.filter(
					(airport) =>
						(airport as Airport).airportCode !==
						(thisAirport as Airport).airportCode
				);
				return [...filteredAirports, thisAirport].slice(
					-1 * recentMax
				) as Airport[];
			});
		}
	};

	useEffect(() => {
		const key = locationSearch
			? (JSON.parse(localStorage.getItem(localStorageKey)!) as Location[])
			: (JSON.parse(localStorage.getItem(localStorageKey)!) as Airport[]);
		const initialAirports = localStorage.getItem(localStorageKey) ? key : [];
		setSelectedAirports(initialAirports);

		if (value) {
			let initialValue: string = '';
			if (locationSearch) {
				initialValue = value
					? ((value as Location).indexedDescription ?? '')
					: '';
			} else {
				initialValue = value
					? `${(value as Airport).cityName?.trim()}, ${(value as Airport).region} (${(value as Airport).airportCode})`
					: '';
			}

			setInitialSelection(initialValue);
			setUserSelection(initialValue);
			setIsPrepoluated(initialValue?.length > 0);
		}
	}, []);

	useEffect(() => {
		if (selectedResult.length > 0) return;
		if (searchQuery?.trim().length >= characterMin) search();

		async function search() {
			locationSearch
				? getCJSLocationSearchUrl({ query: searchQuery, ...locationSearch })
						.then((results) => {
							setIsSearching(false);
							const returnedData = results?.data?.items || [];

							setSuggestions(validateLocations(returnedData));
							setShowResults(returnedData.length > 0);
						})
						.catch((e) => {
							setIsSearching(false);
							console.error(e);
						})
				: getAirportsByTerm(searchQuery)
						.then((results) => {
							setIsSearching(false);
							const returnedData = results?.data || [];

							setSuggestions(returnedData);
							setShowResults(returnedData.length > 0);
						})
						.catch((e) => {
							setIsSearching(false);
							console.error(e);
						});
		}
	}, [searchQuery, userSelection]);

	useEffect(() => {
		setIsSearching(true);
	}, [searchQuery]);

	useEffect(() => {
		if (suggestions.length > 0 && searchQuery?.trim().length < characterMin) {
			setSuggestions([]);
			setShowResults(false);
		}

		const isNoResults =
			suggestions.length === 0 &&
			userSelection.length >= characterMin &&
			!selectedResult;
		setNoResults(isNoResults);
		handlePopOver('searchChange');
	}, [suggestions, searchQuery]);

	useEffect(() => {
		if (userSelection !== selectedResult) {
			setSelectedResult('');
		}
	}, [userSelection, selectedResult]);

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (!suggestions) {
				return;
			}

			const maxIndex = suggestions.length - 1;
			const { key } = event;

			switch (key) {
				case 'ArrowUp':
					if (focusedIndex !== null && focusedIndex > 0) {
						setFocusedIndex((prev: number) => prev! - 1);
					}
					break;
				case 'ArrowDown':
					if (focusedIndex !== null && focusedIndex < maxIndex) {
						setFocusedIndex((prev: number) => prev! + 1);
					} else if (focusedIndex === null) {
						setFocusedIndex(0);
					}
					break;
				default:
					break;
			}
		};

		if (showPopOver) {
			window.addEventListener('keydown', handleKeyDown);
		}

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [showPopOver, focusedIndex]);

	useEffect(() => {
		setShowRecentSearches(selectedAirports.length > 0);
		localStorage.setItem(localStorageKey, JSON.stringify(selectedAirports));
	}, [selectedAirports]);

	useEffect(() => {
		setShowRecentSearches(showResults ? false : selectedAirports.length > 0);
	}, [showResults]);

	useEffect(() => {
		if (selectedResult.length) {
			setShowResults(false);
			setSuggestions([]);
		}
	}, [selectedResult]);

	return (
		<div onBlur={handleBlur} data-cy="typeahead-component">
			{isMobileLayout && (
				<CriteriaTriggerComponent
					label={popoverTitle}
					value={userSelection}
					hasError={hasError}
					errorMessage={feedback}
					onClick={(): void => handlePopOver('criteriaClick')}
					iconChild={iconChild}
					aria={{
						controls: popOverId,
						expanded: showPopOver
					}}
				/>
			)}

			{!isMobileLayout && (
				<TextFieldComponent
					{...{
						label,
						placeholder
					}}
					{...{ id }}
					value={userSelection}
					clearable={true}
					autoComplete="off"
					spellCheck={false}
					placeholder={placeholder}
					onChange={(e) => {
						setUserSelection(e.target.value);
						setSelectedResult('');
						setIsPrepoluated(false);
					}}
					onFocus={handleFocus}
					onClear={handleClear}
					feedback={!showPopOver && feedback}
					hasError={hasError}
					role="combobox"
					aria-controls={popOverId}
					aria-expanded={showPopOver}
					aria-label={`${label} - begin entering your search to receive results`}
					aria-autocomplete="list"
					aria-haspopup="true"
				/>
			)}
			<PopOverComponent
				id={popOverId}
				isVisible={showPopOver}
				type={PopOverType.Selector}
				title={popoverTitle}
				scroll={true}
				minWidth={375}
				onClose={(): void => handlePopOver('popOverClose')}>
				<>
					{isMobileLayout && (
						<TypeaheadMobileInput>
							<TextFieldComponent
								{...{
									label,
									placeholder
								}}
								{...{ id }}
								value={userSelection}
								clearable={true}
								autoComplete="off"
								spellCheck={false}
								placeholder={placeholder}
								onChange={(e) => {
									setUserSelection(e.target.value);
									setSelectedResult('');
									setIsPrepoluated(false);
								}}
								onClear={handleClear}
								onFocus={handleFocus}
								feedback={feedback}
								hasError={hasError}
								aria-controls={popOverId}
							/>
						</TypeaheadMobileInput>
					)}
					{isSearching && noResults && (
						<TypeaheadSearching>
							<IconFeedbackLoadingComponent data-search />
							<TypographyComponent
								element={'p'}
								variant={TypographyVariant.Body}
								size={TypographySize.Sml}
								weight={TypographyWeight.Regular}>
								Searching
							</TypographyComponent>
						</TypeaheadSearching>
					)}
					{noResults && !isSearching && !isPrepoluated && (
						<PopoverFeedback message={noResultsLabel} />
					)}
					{showResults && !noResults && !locationSearch && (
						<TypeaheadAirportResults
							results={suggestions as Airport[]}
							visible={showPopOver}
							onClick={(e) => handleResultClick(e, 'resultClick')}
							{...{ focusedIndex }}
						/>
					)}
					{showRecentSearches &&
						(!noResults || isPrepoluated) &&
						!locationSearch && (
							<>
								<TypeaheadRecentTitle>
									<TypographyComponent
										variant={TypographyVariant.Heading}
										size={TypographySize.Xxs}
										weight={TypographyWeight.Medium}>
										{historyTitle}
									</TypographyComponent>
								</TypeaheadRecentTitle>
								<TypeaheadAirportResults
									results={selectedAirports as Airport[]}
									visible={showPopOver}
									onClick={(e) => handleResultClick(e, 'recentClick')}
									{...{ focusedIndex }}
								/>
							</>
						)}
					{showResults && !noResults && locationSearch && (
						<TypeaheadLocationResults
							results={suggestions as Location[]}
							visible={showPopOver}
							onClick={(e) => handleResultClick(e, 'resultClick')}
							{...{ focusedIndex }}
						/>
					)}
					{showRecentSearches &&
						(!noResults || isPrepoluated) &&
						locationSearch && (
							<>
								<TypeaheadRecentTitle>
									<TypographyComponent
										variant={TypographyVariant.Heading}
										size={TypographySize.Xxs}
										weight={TypographyWeight.Medium}>
										{historyTitle}
									</TypographyComponent>
								</TypeaheadRecentTitle>
								<TypeaheadLocationResults
									results={selectedAirports as Location[]}
									visible={showPopOver}
									onClick={(e) => handleResultClick(e, 'recentClick')}
									isRecent={true}
									{...{ focusedIndex }}
								/>
							</>
						)}
				</>
			</PopOverComponent>
		</div>
	);
};

export default TypeaheadComponent;

interface TypeaheadAirportResultsProps {
	results: Airport[];
	visible: boolean;
	onClick: (airport: Airport) => void;
	focusedIndex: number;
}

const TypeaheadAirportResults = ({
	results,
	visible,
	onClick,
	focusedIndex
}: TypeaheadAirportResultsProps) => {
	const resultRefs = useRef<(HTMLButtonElement | null)[]>([]);

	useEffect(() => {
		if (focusedIndex !== null && resultRefs.current[focusedIndex]) {
			resultRefs.current[focusedIndex].focus();
		}
	}, [focusedIndex]);

	return (
		<TypeaheadResultsList data-cy="typeahead-results" role="listbox">
			{results?.map((result, index) => (
				<TypeaheadResult key={index} data-iata={result?.airportCode}>
					<TypeaheadResultButton
						type="button"
						onClick={() => onClick(result)}
						ref={(el) => (resultRefs.current[index] = el)}
						tabIndex={visible ? 0 : -1}
						role="option">
						<IconTravelAirportComponent />
						<TypeaheadResultLabel>
							{result?.cityName?.trim()}, {result?.region} (
							{result?.airportCode})
						</TypeaheadResultLabel>
					</TypeaheadResultButton>
				</TypeaheadResult>
			))}
		</TypeaheadResultsList>
	);
};

interface TypeaheadLocationResultsProps {
	results: Location[];
	visible: boolean;
	onClick: (result: Location) => void;
	isRecent?: boolean;
	focusedIndex: number;
}

const TypeaheadLocationResults = ({
	results,
	visible,
	onClick,
	focusedIndex,
	isRecent = false
}: TypeaheadLocationResultsProps) => {
	const resultRefs = useRef<(HTMLButtonElement | null)[]>([]);
	const hotels = results.filter(
		(result) => result?.locationMetadata?.hotelDetails
	);
	const destinations = results.filter(
		(result) => !result?.locationMetadata?.hotelDetails
	);

	const resultList = (
		<TypeaheadResultsList>
			{destinations.length > 0 && (
				<>
					<TypeaheadRecentTitle>
						<TypographyComponent
							variant={TypographyVariant.Heading}
							size={TypographySize.Xxs}
							weight={TypographyWeight.Medium}>
							Destinations
						</TypographyComponent>
					</TypeaheadRecentTitle>
					{destinations.map((result, index) => (
						<TypeaheadResult
							key={index}
							data-location={result?.locationUrlDescription}>
							<TypeaheadResultButton
								type="button"
								onClick={() => onClick(result)}
								ref={(el) => (resultRefs.current[index] = el)}
								tabIndex={visible ? 0 : -1}>
								<IconTravelMapMarkerComponent />
								<TypeaheadResultLabel>
									{result?.indexedDescription}
								</TypeaheadResultLabel>
							</TypeaheadResultButton>
						</TypeaheadResult>
					))}
				</>
			)}
			{hotels.length > 0 && (
				<>
					<TypeaheadRecentTitle>
						<TypographyComponent
							variant={TypographyVariant.Heading}
							size={TypographySize.Xxs}
							weight={TypographyWeight.Medium}>
							Hotels
						</TypographyComponent>
					</TypeaheadRecentTitle>
					{hotels.map((result, index) => (
						<TypeaheadResult key={destinations.length + index}>
							<TypeaheadResultButton
								type="button"
								onClick={() => onClick(result)}
								ref={(el) =>
									(resultRefs.current[destinations.length + index] = el)
								}
								tabIndex={visible ? 0 : -1}>
								<IconTravelHotelComponent />
								<TypeaheadResultLabel>
									{result?.indexedDescription}
								</TypeaheadResultLabel>
							</TypeaheadResultButton>
						</TypeaheadResult>
					))}
				</>
			)}
		</TypeaheadResultsList>
	);

	const recentList = (
		<TypeaheadResultsList>
			{results.map((result, index) => (
				<TypeaheadResult key={index}>
					<TypeaheadResultButton
						type="button"
						onClick={() => onClick(result)}
						ref={(el) => (resultRefs.current[index] = el)}
						tabIndex={visible ? 0 : -1}>
						{result?.locationMetadata?.hotelDetails ? (
							<IconTravelHotelComponent />
						) : (
							<IconTravelMapMarkerComponent />
						)}
						<TypeaheadResultLabel>
							{result?.indexedDescription}
						</TypeaheadResultLabel>
					</TypeaheadResultButton>
				</TypeaheadResult>
			))}
		</TypeaheadResultsList>
	);

	useEffect(() => {
		if (focusedIndex !== null && resultRefs.current[focusedIndex]) {
			resultRefs.current[focusedIndex].focus();
		}
	}, [focusedIndex]);

	return isRecent ? recentList : resultList;
};

export const handleTypeaheadFocus = (id: string): void => {
	const element = document.getElementById(id);

	if (element) {
		element.focus();
	} else {
		console.error(`Element with ID ${id} not found.`);
	}
};
